import { useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { describeFailure } from '@/lib/describe-failure';
import {
  getQualityMetricConfiguration,
  setQualityMetricConfiguration,
  setQualityMetricCredentials,
  type QualityMetricRunView,
} from '@/api/quality-metric.api.private';
import { PULL_MAPPERS } from 'shared/constants/quality-metric.constants';
import type { QualityMetricOrganizationConfiguration } from 'shared/api/quality-metric-rq.service.api';
import { NO_MAPPER, SOURCE, mapperLabel, type Mapper } from './constants';
import RunsSection from './runs-section';
import RunEditor from './run-editor';
import { useRuns } from './use-runs';

const MAPPER_SCHEMA = z.object({ mapper: z.string() });
const CONFIGURATION_NAME_SCHEMA = z.object({ configurationName: z.string().trim().max(200) });
const CREDENTIALS_SCHEMA = z.object({
  username: z.string().min(1, { message: 'Username is required.' }).max(512),
  password: z.string().min(1, { message: 'Password is required.' }).max(4096),
});

/** The values a confirmation dialog is currently asking the operator about. */
interface PendingConfirmation {
  title: string;
  description: string;
  run: () => void;
}

/**
 * Everything that configures this organization's quality-metric ingestion:
 * the parser, the MPOG connection and the scheduled pulls.
 *
 * One panel rather than a stack of cards, because the three only make sense
 * together — a site name with no schedule never pulls, and a schedule with no
 * credentials ticks into a 404. The card behind it shows the summary and
 * nothing else, so there is exactly one place to change any of it.
 *
 * @param props.open - Whether the sheet is showing.
 * @param props.onOpenChange - Called with the next open state. The card uses
 *   the close to re-read the configuration, since run writes never hand a
 *   fresh document back.
 * @param props.configuration - The stored document, or `null` for an
 *   organization that has none yet.
 * @param props.onConfigurationChange - Called with the document returned by
 *   each write, so the card's summary stays in step.
 * @returns The sheet, with the run editor swapped in while editing a run.
 * @example
 * // Mounted only while open, so the forms re-seed from the stored document on
 * // every open and no typed secret survives a close.
 * {open && (
 *   <IngestionSheet
 *     open={open}
 *     onOpenChange={onSheetOpenChange}
 *     configuration={configuration}
 *     onConfigurationChange={setConfiguration}
 *   />
 * )}
 */
export default function IngestionSheet({
  open,
  onOpenChange,
  configuration,
  onConfigurationChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configuration: QualityMetricOrganizationConfiguration | null;
  onConfigurationChange: (next: QualityMetricOrganizationConfiguration | null) => void;
}) {
  const { toast } = useToast();
  const runsState = useRuns({ source: SOURCE });

  const [view, setView] = useState<'main' | 'run'>('main');
  const [editingRun, setEditingRun] = useState<QualityMetricRunView | null>(null);
  const [savingMapper, setSavingMapper] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [credentialsUnknown, setCredentialsUnknown] = useState(false);
  // Holds the values a confirmation is currently asking about, so the dialog
  // sends exactly what the user saw rather than re-reading the form.
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  const defaultMapper = configuration?.mapper?.default ?? null;
  // `?? ''` rather than a destructuring default: the stored value is nullable,
  // and `null` is what clearing the field writes.
  const configurationName = configuration?.[SOURCE]?.configurationName ?? '';
  const secretName = configuration?.[SOURCE]?.secretName;
  /**
   * `unknown` is the gap between a credential write succeeding and the read
   * after it failing: the credentials exist, but `secretName` is not in the
   * copy on screen. Showing the pre-write badge there reads as "not set" and
   * invites a pointless second rotation, so the uncertainty is shown instead.
   */
  const credentialsStatus: 'set' | 'unset' | 'unknown' = credentialsUnknown
    ? 'unknown'
    : secretName
      ? 'set'
      : 'unset';
  // `unknown` only happens after a write that succeeded, so it is not "unset".
  const hasCredentials = credentialsStatus !== 'unset';
  const isPullEnabled = !!configurationName && hasCredentials;

  const mapperForm = useForm({
    resolver: zodResolver(MAPPER_SCHEMA),
    defaultValues: { mapper: defaultMapper ?? NO_MAPPER },
  });
  const nameForm = useForm({
    resolver: zodResolver(CONFIGURATION_NAME_SCHEMA),
    defaultValues: { configurationName },
  });
  const credentialsForm = useForm({
    resolver: zodResolver(CREDENTIALS_SCHEMA),
    defaultValues: { username: '', password: '' },
  });

  // Re-seeded from the response, not from what was typed, so a server-side
  // normalisation shows immediately.
  const applyConfiguration = (next: QualityMetricOrganizationConfiguration | null) => {
    onConfigurationChange(next);
    setCredentialsUnknown(false);
    mapperForm.reset({ mapper: next?.mapper?.default ?? NO_MAPPER });
    nameForm.reset({ configurationName: next?.[SOURCE]?.configurationName ?? '' });
  };

  // Merged field by field server-side, so each section sends only its own
  // block, and the response is the updated document.
  const save = async (
    block: Partial<QualityMetricOrganizationConfiguration>,
    { onError, onSuccess }: { onError: string; onSuccess: string },
  ) => {
    const { data, error, code } = await setQualityMetricConfiguration({ data: block });

    if (error) {
      toast({
        title: onError,
        description: describeFailure({ data, code }),
        variant: 'destructive',
      });
      return;
    }

    applyConfiguration(data);
    toast({ title: onSuccess });
  };

  const saveMapper = async (mapper: Mapper | null) => {
    setSavingMapper(true);
    await save(
      { mapper: { default: mapper } },
      { onError: 'Failed to update the default parser', onSuccess: 'Default parser updated' },
    );
    setSavingMapper(false);
  };

  const saveConfigurationName = async (name: string | null) => {
    setSavingName(true);
    await save(
      { [SOURCE]: { configurationName: name } },
      {
        onError: 'Failed to update the MPOG site name',
        onSuccess: 'MPOG site name updated',
      },
    );
    setSavingName(false);
  };

  const saveCredentials = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    setSavingCredentials(true);
    const { data, error, code } = await setQualityMetricCredentials({
      source: SOURCE,
      username,
      password,
    });

    if (error) {
      setSavingCredentials(false);
      // Left as typed on purpose: a 500 wrote nothing, and clearing would mean
      // retyping a 4096-char secret to retry.
      toast({
        title: 'Failed to store the credentials',
        // The server message only — never echo what was sent.
        description: describeFailure({ data, code }),
        variant: 'destructive',
      });
      return false;
    }

    // Saved, so the secret leaves component state immediately.
    credentialsForm.reset({ username: '', password: '' });

    // 204 with no body, so `secretName` only arrives on a re-read. This is also
    // the call that creates the document for an organization that had none.
    const refreshed = await getQualityMetricConfiguration();
    setSavingCredentials(false);

    if (refreshed.error) {
      // The write succeeded — only the read after it failed.
      setCredentialsUnknown(true);
      toast({
        title: 'Credentials stored, but the status could not be refreshed',
        description: 'Reload the page to see the current credential status.',
      });
      return true;
    }

    applyConfiguration(refreshed.data);
    toast({ title: 'Credentials stored' });
    return true;
  };

  const confirmAndRun = () => {
    if (!pending) return;
    const { run } = pending;
    setPending(null);
    run();
  };

  // Every confirmed action re-points or breaks ingestion for the whole
  // organization, so each one names what it is about to change.
  const onMapperSubmit = ({ mapper }: { mapper: string }) => {
    const next = mapper === NO_MAPPER ? null : (mapper as Mapper);
    if (next === defaultMapper) return;

    const effect = next
      ? `Every metric without a parser of its own will be read with ${mapperLabel(next)} from the next sync run. The current default is ${defaultMapper ? mapperLabel(defaultMapper) : 'not set'}.`
      : 'The default parser is removed. Any pull-enabled metric without a parser of its own will be skipped by the sync service.';

    setPending({
      title: 'Change the default parser?',
      description: effect,
      run: () => saveMapper(next),
    });
  };

  const onNameSubmit = ({ configurationName: next }: { configurationName: string }) => {
    const trimmed = next.trim();
    if (trimmed === configurationName) return;

    if (!trimmed) {
      setPending({
        title: 'Clear the MPOG site name?',
        description:
          'Without it the organization is no longer pull-enabled and the sync service will skip it entirely, however its runs are scheduled.',
        run: () => saveConfigurationName(null),
      });
      return;
    }

    saveConfigurationName(trimmed);
  };

  // No extra confirmation: opening the dialog and typing both fields is already
  // the deliberate step.
  const onCredentialsSubmit = async (values: { username: string; password: string }) => {
    const ok = await saveCredentials(values);
    if (ok) setCredentialsOpen(false);
  };

  const onCredentialsOpenChange = (next: boolean) => {
    setCredentialsOpen(next);
    // Never leave a typed secret sitting in a closed dialog.
    if (!next) credentialsForm.reset({ username: '', password: '' });
  };

  const saveRunAndReturn = async (payload: Parameters<typeof runsState.saveRun>[1]) => {
    const message = await runsState.saveRun(editingRun?._id ?? null, payload);
    if (!message) {
      setEditingRun(null);
      setView('main');
    }
    return message;
  };

  const missing = [!configurationName && 'a site name', !hasCredentials && 'credentials'].filter(
    Boolean,
  );
  const prerequisites = missing.length
    ? `This organization is missing ${missing.join(' and ')}, so a tick would pull nothing — the sync service skips an organization until both are set.`
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-3xl">
        {/* pr-14 keeps the header clear of the Sheet's own close button. */}
        <SheetHeader className="space-y-1 p-6 pr-14">
          <SheetTitle>Quality metrics ingestion</SheetTitle>
          <SheetDescription>
            How this organization&apos;s metrics are pulled: the parser its exports are read with,
            the MPOG site they come from, and when that site is pulled.
          </SheetDescription>
        </SheetHeader>

        {view === 'run' ? (
          <RunEditor
            run={editingRun}
            timeZone={runsState.timeZone}
            onSave={saveRunAndReturn}
            onCancel={() => {
              setEditingRun(null);
              setView('main');
            }}
          />
        ) : (
          <div className="flex-1 space-y-6 overflow-y-auto p-6 pt-0">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Parser</span>
              <Badge variant={defaultMapper ? 'secondary' : 'destructive'}>
                {defaultMapper ? mapperLabel(defaultMapper) : 'No default parser'}
              </Badge>
              <span className="text-muted-foreground">MPOG</span>
              <Badge variant={isPullEnabled ? 'default' : 'destructive'}>
                {isPullEnabled ? 'Pull-enabled' : 'Not pull-enabled'}
              </Badge>
              <Badge variant={credentialsStatus === 'set' ? 'secondary' : 'outline'}>
                {credentialsStatus === 'set'
                  ? 'Credentials set'
                  : credentialsStatus === 'unknown'
                    ? 'Credentials status unknown'
                    : 'Credentials not set'}
              </Badge>
            </div>

            {!defaultMapper && (
              <p className="text-muted-foreground text-sm">
                With no default parser, any pull-enabled metric that does not set its own is
                skipped.
              </p>
            )}
            {/* The document's own timestamp: it moves on any change here, not on
                the credentials alone, so it is labelled for the configuration. */}
            {configuration?.updatedAt && (
              <p className="text-muted-foreground text-xs">
                Configuration last updated {format(new Date(configuration.updatedAt), 'PPp')}
              </p>
            )}

            <Separator />

            <Form {...mapperForm}>
              <form onSubmit={mapperForm.handleSubmit(onMapperSubmit)} className="space-y-4">
                <FormField
                  control={mapperForm.control}
                  name="mapper"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">Default parser</FormLabel>
                      <FormDescription>
                        The parser the sync service runs on this organization&apos;s exports. It is
                        set for every organization that pulls, whatever the source — individual
                        metrics override it when they come through a different one.
                      </FormDescription>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NO_MAPPER}>Not set</SelectItem>
                          {PULL_MAPPERS.map(mapper => (
                            <SelectItem key={mapper} value={mapper}>
                              {mapperLabel(mapper)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={savingMapper || !mapperForm.formState.isDirty}>
                  {savingMapper ? 'Saving...' : 'Save parser'}
                </Button>
              </form>
            </Form>

            <Separator />

            <Form {...nameForm}>
              <form onSubmit={nameForm.handleSubmit(onNameSubmit)} className="space-y-4">
                <FormField
                  control={nameForm.control}
                  name="configurationName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-bold">MPOG site name</FormLabel>
                      <FormDescription>
                        The site MPOG pulls from, exactly as MPOG names it, for example &quot;Henry
                        Ford Health System - Detroit&quot;.
                      </FormDescription>
                      <FormControl>
                        <Input {...field} placeholder="Not set" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" disabled={savingName || !nameForm.formState.isDirty}>
                  {savingName ? 'Saving...' : 'Save site name'}
                </Button>
              </form>
            </Form>

            <div className="space-y-4">
              <div className="space-y-0.5">
                <p className="text-sm font-bold">MPOG credentials</p>
                <p className="text-muted-foreground text-sm">
                  Stored in Secret Manager and never shown again.{' '}
                  {hasCredentials
                    ? 'Setting them again replaces what is stored.'
                    : 'Needed before the sync service can pull this organization.'}
                </p>
              </div>
              <Button variant="outline" onClick={() => onCredentialsOpenChange(true)}>
                {hasCredentials ? 'Rotate credentials' : 'Set credentials'}
              </Button>
            </div>

            <Separator />

            <RunsSection
              state={runsState}
              prerequisites={prerequisites}
              onAdd={() => {
                setEditingRun(null);
                setView('run');
              }}
              onEdit={run => {
                setEditingRun(run);
                setView('run');
              }}
            />
          </div>
        )}
      </SheetContent>

      <Dialog open={!!pending} onOpenChange={next => !next && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending?.title}</DialogTitle>
            <DialogDescription>{pending?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button onClick={confirmAndRun}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={credentialsOpen} onOpenChange={onCredentialsOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {hasCredentials ? 'Rotate MPOG credentials' : 'Set MPOG credentials'}
            </DialogTitle>
            <DialogDescription>
              {hasCredentials
                ? 'These replace the stored credentials. If they are wrong the next sync run fails, and the previous ones cannot be recovered from here.'
                : 'Stored in Secret Manager. They are never shown again after saving.'}
            </DialogDescription>
          </DialogHeader>
          <Form {...credentialsForm}>
            <form
              onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)}
              className="space-y-4">
              <FormField
                control={credentialsForm.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input {...field} autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={credentialsForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input {...field} type="password" autoComplete="new-password" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onCredentialsOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={savingCredentials}>
                  {savingCredentials ? 'Saving...' : 'Save credentials'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
}
